import * as tus from "tus-js-client";
import { DetailedError, type Upload } from "tus-js-client";
import { action, computed, makeObservable, observable } from "mobx";
import { newShortGuid } from "../util/types/guid";
import { DicomFileReader } from "./DicomFileReader";
import { DicomUploadTusFingerprintStore } from "./DicomUploadTusFingerprintStore";
import { isNullOrWhiteSpace } from "../util/functions/isNullOrWhiteSpace";
import { DicomTags } from "../app/viewer/network/contracts/dicom/DicomDataSet";
import { DicomMediaStorageStandardSopClass } from "../app/viewer/network/contracts/dicom/DicomMediaStorageStandardSopClass";

export class FileUpload {
    @observable
    fileGuid: string;

    @observable
    fileIsADicomFile: boolean = false;

    @observable
    fileIsADicomDirFile: boolean = false;

    @observable
    checking: boolean = true;

    @observable
    selected: boolean = false; //To support selected non-dicom files to easily add to a study

    @observable
    status: UploadStatus = UploadStatus.NotStarted;

    @observable
    progress = 0;

    @observable
    bytesUploaded = 0;

    file: File;
    name: string;
    type: string;
    size: number;
    keyInLocalStorage: string | null = null;

    sourceId = 0;
    uploadCode: string | null = null;
    emailAddress = "";
    mobilePhone = "";

    private _upload: Upload | null = null; // will remain null when file is not uploadable

    private readonly _endpoint: string;
    private readonly _dicomFileReader: DicomFileReader;

    constructor(file: File, endpoint: string, sourceId: number, uploadCode: string | null) {
        makeObservable(this);

        this.fileGuid = newShortGuid();

        this.file = file;
        this.name = file.name;
        this.type = file.type;
        this.size = file.size;

        this._endpoint = endpoint;
        this._dicomFileReader = new DicomFileReader(file);

        this.sourceId = sourceId;
        this.uploadCode = uploadCode;
    }

    @computed
    public get fileNameLengthIsOk(): boolean {
        return this.name.length < 255;
    }

    public getDicomTagValue(dicomTag: DicomTags): string {
        if (!this.fileIsADicomFile) return "";
        return this._dicomFileReader.getDicomTag(dicomTag)?.value ?? "";
    }

    @computed
    public get uploadable(): boolean {
        return !this.checking && this.fileIsSupported && !this.fileIsADicomDirFile && this.fileNameLengthIsOk && !this.fileError;
    }

    @action
    private setStatus(status: UploadStatus) {
        if (this.status !== status) {
            console.debug(`ℹ️ status of file ${this.file.name} from ${this.status} to ${status}`);
            this.status = status;
        }
    }

    @action
    private setProgress(progress: number) {
        this.progress = progress;
    }

    @action
    private setBytesUploaded(bytes: number): void {
        this.bytesUploaded = bytes;
    }

    @action
    startUpload(patientGuid: string): void {
        if (!this.uploadable) return;

        if (!this._upload) {
            this.makeTusUpload(patientGuid);
        }
        if (!this._upload) return; //Should never happen since we make it right before this, but just in case...
        if (this.status === UploadStatus.Done || this.status == UploadStatus.ValidatingFiles) return; //Already done, don't trigger again or it will send the file again
        this.setStatus(UploadStatus.Started);
        this._upload.findPreviousUploads().then(previousUploads => {
            if (previousUploads.length !== 0) {
                // most recent upload is always at index 0
                this._upload!.resumeFromPreviousUpload(previousUploads[0]);
            }
            this._upload!.start();
        });
    }

    public pauseUpload(): void {
        if (!this._upload) return;
        this._upload.abort().then(r => this.setStatus(UploadStatus.Paused));
    }

    @action
    public cancelUpload(): void {
        if (this._upload != null && this.status === UploadStatus.Started) {
            this._upload.abort();
        }
        this._upload = null;
        this.setStatus(UploadStatus.Cancelled);
    }

    @action
    queueUpload(): void {
        if (!this.uploadable) return;

        if (this.status === UploadStatus.NotStarted || this.status === UploadStatus.Paused) {
            this.setStatus(UploadStatus.Queued);
        }
    }

    @action
    retryUpload(): void {
        if (!this.uploadable) return;
        this.setStatus(UploadStatus.NotStarted);
    }

    public async checkAndMakeFileUploadReady(): Promise<void> {
        await this.readFileAndDoChecks();
    }

    @computed
    public get patientId(): string {
        return this._patientId;
    }

    @computed
    public get studyId(): string {
        if (!isNullOrWhiteSpace(this._studyId)) return this._studyId;
        return this._studyIdForNonDicomFile;
    }

    @observable
    private _studyIdForNonDicomFile: string = ""; //StudyId for non-dicom files assigned to a study

    @observable
    private _studyId: string = ""; //StudyId for dicom files

    @observable
    private _seriesInstanceUid: string = ""; //Series instance Uid for dicom files

    @observable
    private _sopInstanceUid: string = ""; //sop instance Uid for dicom files

    @action
    public setStudyIdForNonDicomFile(id: string) {
        this._studyIdForNonDicomFile = id;
    }

    @action
    public setStudyId(id: string) {
        this._studyId = id;
    }

    @observable
    private _patientId: string = "";

    @action
    public setPatientId(id: string) {
        this._patientId = id;
    }

    @computed
    public get fileIsSupported() {
        return (
            this.fileIsADicomFile ||
            this.file.type === "application/pdf" || //.pdf
            this.file.type === "image/png" || //.png
            this.file.type === "image/tiff" || //.tiff / .tif
            this.file.type === "image/jpeg"
        ); //.jpg / .jpeg / .jpe
    }

    @action
    private async readFileAndDoChecks(): Promise<void> {
        this.setChecking(true);

        await this._dicomFileReader.readFile();
        const fileIsADicomFile = this._dicomFileReader.dataSet !== null;
        this.setFileIsADicomFile(fileIsADicomFile);

        if (fileIsADicomFile) {
            this.setStudyId(this.getDicomTagValue(DicomTags.StudyInstanceUID));
            this.setPatientId(this.getDicomTagValue(DicomTags.PatientID));
            this._seriesInstanceUid = this.getDicomTagValue(DicomTags.SeriesInstanceUID);
            this._sopInstanceUid = this.getDicomTagValue(DicomTags.SOPInstanceUID);
        }

        const fileIsADicomDir = this.getDicomTagValue(DicomTags.MediaStorageSOPClassUID) === DicomMediaStorageStandardSopClass.MediaStorageDirectoryStorage;
        this.setFileIsADicomDirFile(fileIsADicomDir);

        this.setChecking(false);
    }

    @action
    public setChecking(value: boolean) {
        this.checking = value;
    }

    @observable
    public _fileError: boolean = false;

    @action
    public setFileError(value: boolean) {
        this._fileError = value;
    }

    @computed
    public get fileError(): boolean {
        if (
            this.fileIsADicomFile &&
            !this.fileIsADicomDirFile &&
            (this._studyId === "" || this._patientId === "" || this._seriesInstanceUid === "" || this._sopInstanceUid === "")
        ) {
            return true;
        }
        return this._fileError;
    }

    @action
    public setSelected(value: boolean) {
        if (!this.selectable) return;
        this.selected = value;
    }

    @computed
    public get selectable(): boolean {
        return !this.fileIsADicomFile && isNullOrWhiteSpace(this.studyId) && isNullOrWhiteSpace(this.patientId);
    }

    @action
    private setFileIsADicomFile(value: boolean) {
        this.fileIsADicomFile = value;
    }

    @action
    private setFileIsADicomDirFile(value: boolean) {
        this.fileIsADicomDirFile = value;
    }

    private makeTusUpload(patientGuid: string) {
        this._upload = new tus.Upload(this.file, {
            endpoint: this._endpoint,
            retryDelays: [0, 3000, 5000, 10000, 20000],
            storeFingerprintForResuming: true, // stores information about uploads in local storage and is needed for resuming
            removeFingerprintOnSuccess: false, // we'll clear the fingerprints manually, once the complete study is uploaded
            metadata: {
                sourceId: this.sourceId.toString(), //Add specific source id,
                uploadCode: this.uploadCode ?? "",
                emailAddress: this.emailAddress,
                mobilePhone: this.mobilePhone,
                patientGuid: patientGuid,
                studyId: this._studyIdForNonDicomFile, //StudyInstanceUID for non-dicom files
                fileFullName: this.file.name, // Needed for TUS
            },
            onError: this.handleUploadError,
            onProgress: this.handleUploadProgress, // Progress means that bytes are sent from the client and does not guarantee that it has arrived at the server
            onChunkComplete: this.handleChunkComplete, // Chunk Complete means that the package successfully arrived at the server
            onSuccess: this.handleUploadSuccess,
            uploadDataDuringCreation: false, // false needed for resumable uploads
            fingerprint: async (file, options) => {
                const fingerprint = await DicomUploadTusFingerprintStore.generate(file, options);
                this.keyInLocalStorage = fingerprint;
                return fingerprint;
            },
        });
    }

    private readonly handleUploadError = (error: Error | DetailedError) => {
        if (error instanceof DetailedError) {
            switch (error.originalResponse?.getBody()) {
                case "RateLimited":
                    this.setStatus(UploadStatus.RateLimited);
                    this.setBytesUploaded(0);
                    this.setProgress(0);
                    break;
                case "IpBlocked":
                    this.setStatus(UploadStatus.IpBlocked);
                    this.setBytesUploaded(0);
                    this.setProgress(0);
                    break;
                case "UploadCodeExpired":
                    this.setStatus(UploadStatus.UploadCodeExpired);
                    this.setBytesUploaded(0);
                    this.setProgress(0);
                    break;
                case "ExceededUploadSizeLimit":
                    this.setStatus(UploadStatus.ExceededUploadSizeLimit);
                    this.setBytesUploaded(0);
                    this.setProgress(0);
                    break;
                default:
                    this.setStatus(UploadStatus.Failed);
                    this.setBytesUploaded(0);
                    this.setProgress(0);
            }
        }

        console.error("Upload failed because: ", error);
    };

    @action
    private readonly handleUploadProgress = (bytesUploaded: number, bytesTotal: number) => {
        const percentage = (bytesUploaded / bytesTotal) * 100;
        this.setBytesUploaded(bytesUploaded);
        this.setStatus(UploadStatus.Started);
        this.setProgress(percentage);
    };

    private readonly handleUploadSuccess = () => {
        this.setBytesUploaded(this.size);
        this.setStatus(UploadStatus.ValidatingFiles);
        this.setProgress(100);
    };

    private readonly handleChunkComplete = (bytesUploaded: number, bytesTotal: number) => {
        const percentage = (bytesUploaded / bytesTotal) * 100;
        this.setBytesUploaded(bytesUploaded);
        this.setStatus(UploadStatus.Started);
        this.setProgress(percentage);
    };

    @computed
    public get removable(): boolean {
        if (!this.uploadable || this.status !== UploadStatus.NotStarted) return false;
        return true;
    }

    public get uploadFingerprint(): string {
        const { _upload } = this;
        if (_upload == null) return "";

        const { url } = _upload;

        if (url == null) return "";

        return url.substring(url.lastIndexOf("/") + 1);
    }

    public maliciousContentFound(): void {
        this.setStatus(UploadStatus.MaliciousContent);
    }

    public notADicomFile(): void {
        this.setStatus(UploadStatus.NotADicomFile);
    }

    public uploadComplete(): void {
        this.setStatus(UploadStatus.Done);
    }

    public uploadFailed(): void {
        this.setStatus(UploadStatus.Failed);
    }
    public uploadExceededUploadSizeLimit(): void {
        this.setStatus(UploadStatus.ExceededUploadSizeLimit);
    }

    get arrivedAtTheBackend(): boolean {
        return (
            this.status === UploadStatus.Done ||
            this.status === UploadStatus.ValidatingFiles ||
            this.status === UploadStatus.MaliciousContent ||
            this.status === UploadStatus.NotADicomFile
        );
    }
}

export enum UploadStatus {
    NotStarted = 0,
    Queued = 1,
    Started = 2,
    Paused = 3,
    Done = 4,
    Failed = 5,
    RateLimited = 6,
    Cancelled = 7,
    ValidatingFiles = 8,
    MaliciousContent = 9,
    NotADicomFile = 10,
    IpBlocked = 11,
    UploadCodeExpired = 12,
    ExceededUploadSizeLimit = 13,
}
